/**
 * MetricCard - Individual metric display with delta and sparkline
 */

import { useMemo } from 'react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { Card, CardContent } from '../../../../../ui/card';
import { ConductScoreCircle } from '../../../../../ui/conduct-score-circle';
import { cn } from '../../../../../../lib/utils';
import { DeltaIndicator } from '../../../../common/components';
import type { MetricCardProps } from '../../types';
import { DELTA_THRESHOLDS } from '../../constants';

// Sparkline color - neutral grey
const SPARKLINE_COLOR = '#94A3B8'; // slate-400

export function MetricCard({
  title,
  value,
  delta,
  deltaLabel,
  format = 'number',
  trendPositive,
  trendData,
  className,
}: MetricCardProps) {
  const formatValue = (val: number | string): string => {
    if (typeof val === 'string') return val;

    switch (format) {
      case 'score':
        return val.toFixed(1);
      case 'percentage':
        return `${val.toFixed(1)}%`;
      case 'number':
      default:
        return val.toLocaleString();
    }
  };

  // Transform trend data for chart
  const chartData = useMemo(() => {
    if (!trendData || trendData.length === 0) return [];
    return trendData.map((point) => ({
      value: point.value,
    }));
  }, [trendData]);

  return (
    <Card className={cn('overflow-hidden rounded-[24px] border-border/70 bg-background/90 shadow-[var(--shadow-card)] backdrop-blur-sm', className)} data-testid="metric-card">
      <CardContent className="p-5 md:p-6">
        <div className="flex items-start justify-between gap-5">
          {/* Left side: metrics */}
          <div className="flex flex-col gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              {title}
            </span>

            <div className="flex items-center gap-3">
              {format === 'score' && typeof value === 'number' ? (
                <ConductScoreCircle
                  score={value}
                  size="md"
                  showDecimal
                />
              ) : (
                <span className="font-jakarta text-4xl font-extrabold tracking-[-0.04em] text-foreground">
                  {formatValue(value)}
                </span>
              )}

              {delta !== undefined && (
                <DeltaIndicator
                  delta={delta}
                  neutralThreshold={DELTA_THRESHOLDS.neutral}
                  isPercentage={format === 'percentage' || format === 'number'}
                  trendPositive={trendPositive}
                />
              )}
            </div>

            {deltaLabel && (
              <span className="text-xs text-muted-foreground">
                {deltaLabel}
              </span>
            )}
          </div>

          {/* Right side: sparkline */}
          {chartData.length > 0 && (
            <div className="flex h-12 w-24 flex-shrink-0 items-center justify-center rounded-full bg-muted/40 px-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={SPARKLINE_COLOR}
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
